import { CircleAlertIcon, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { errorMessage } from "@/lib/hooks.ts";
import { cn } from "@/lib/utils.ts";

/** A calm empty state: icon, one line, and an optional action. */
export function EmptyState({
  icon: Icon,
  title,
  text,
  action,
  className,
}: {
  icon?: LucideIcon;
  title?: ReactNode;
  text?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 px-6 py-10 text-center", className)}>
      {Icon ? (
        <span className="mb-1 flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon className="size-5" aria-hidden />
        </span>
      ) : null}
      {title ? <p className="text-sm font-semibold text-foreground">{title}</p> : null}
      {text ? <p className="max-w-sm text-[13px] leading-relaxed text-muted-foreground">{text}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/** A failed load with the reason and a retry button. */
export function ErrorState({
  error,
  onRetry,
  title,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: ReactNode;
  className?: string;
}) {
  const { t } = useI18n();
  return (
    <div role="alert" className={cn("flex flex-col items-center justify-center gap-2 px-6 py-10 text-center", className)}>
      <span className="mb-1 flex size-10 items-center justify-center rounded-full bg-danger-soft text-danger">
        <CircleAlertIcon className="size-5" aria-hidden />
      </span>
      <p className="text-sm font-semibold">{title ?? t.errors.loadFailed}</p>
      <p className="max-w-sm text-[13px] leading-relaxed text-muted-foreground">{errorMessage(error, t)}</p>
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
          {t.errors.tryAgain}
        </Button>
      ) : null}
    </div>
  );
}

/** Title and subtitle at the top of a settings, usage, or admin page. */
export function PageHeading({
  title,
  subtitle,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between", className)}>
      <div className="min-w-0">
        <h1 className="text-xl font-bold tracking-[-0.02em] text-foreground sm:text-[22px]">{title}</h1>
        {subtitle ? <p className="mt-1 text-[13px] text-muted-foreground">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** A labelled number tile for usage and admin pages. */
export function StatTile({
  label,
  value,
  hint,
  tone = "default",
  icon: Icon,
  action,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "default" | "warning" | "danger" | "primary";
  icon?: LucideIcon;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-xl border bg-card px-4 py-3.5 shadow-[0_1px_2px_rgba(15,27,45,0.04)]">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[12.5px] font-semibold text-muted-foreground">{label}</span>
        {Icon ? <Icon className="size-4 shrink-0 text-muted-foreground/70" aria-hidden /> : null}
      </div>
      <span
        className={cn(
          "truncate text-[22px] leading-tight font-bold tracking-[-0.02em]",
          tone === "warning" && "text-warning",
          tone === "danger" && "text-danger",
          tone === "primary" && "text-primary",
        )}
      >
        {value}
      </span>
      {hint || action ? (
        <div className="flex min-h-5 items-center justify-between gap-2">
          {hint ? <span className="truncate text-xs text-muted-foreground">{hint}</span> : <span />}
          {action}
        </div>
      ) : null}
    </div>
  );
}
